'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SDKResponse } from '@commercetools/frontend-sdk';
import {
  clearCustomizationData,
  clearLegacyCustomizationData,
  getCustomizationData,
  setCustomizationData,
} from 'helpers/utils/customizationStorage';
import { ProjectSettings } from 'shared/types/ProjectSettings';

type HostMessageHandler = (event: MessageEvent) => void;
export type CheckoutFlow = 'standard' | 'express' | 'print-to-retail';

interface MessagePortContextType {
  isReady: boolean;
  sendToHost: ({ type, payload }: { type: string; payload?: object }) => void;
  sendCheckoutStart: (flow: CheckoutFlow) => void;
  sendCheckoutComplete: () => void;
  sendCartStatus: (itemCount: number) => void;
  sendConfirmBeforeClose: (state: boolean) => void;
  sendExitStore: () => void;
  sendRefreshPhotos: (preselectedPhotoIds: string[]) => void;
  subscribeToHostMessages: (handler: HostMessageHandler) => () => void;
}

interface ChildProps {
  children: ReactNode;
  projectSettingsSDK: SDKResponse<ProjectSettings>;
}

// We queue messages until we have loaded the list of allowed origins, so we can validate them.
// We don't expect many messages to queue in that time, but we allow up to 100
const MAX_PENDING_MESSAGES = 100;

// Initialize context with the correct type (starts as undefined)
const MessagePortContext = createContext<MessagePortContextType | undefined>(undefined);

export const MessageChannelPortProvider = ({ children, projectSettingsSDK }: ChildProps) => {
  const [initialized, setInitialized] = useState(false);
  const [origin, setOrigin] = useState('*');
  const pendingMessagesRef = useRef<MessageEvent[]>([]);
  const projectSettings = !projectSettingsSDK?.isError ? projectSettingsSDK?.data : {};
  const targetOriginSetting = projectSettings?.MESSAGING_TARGET_ORIGIN;
  const targetOriginSettingRef = useRef(targetOriginSetting);
  const messageHandlersRef = useRef<Set<HostMessageHandler>>(new Set());

  useEffect(() => {
    targetOriginSettingRef.current = targetOriginSetting;
  }, [targetOriginSetting]);

  const googlePhotosTtlHours = projectSettings?.GOOGLE_PHOTOS_TTL_HOURS;
  const googlePhotosRemainingHours = projectSettings?.GOOGLE_PHOTOS_REMAINING_HOURS;
  const getGooglePhotosSettings = (ttlHours = 12, remainingHours = 4) => {
    if (remainingHours > ttlHours) {
      console.error(
        `Remaining hours (${remainingHours}) cannot be greater than TTL hours (${ttlHours}). Resetting to default values.`,
      );
      return {
        ttlHours: 12,
        remainingHours: 4,
      };
    }
    return {
      ttlHours,
      remainingHours,
    };
  };
  const googlePhotosSettingsRef = useRef(getGooglePhotosSettings(googlePhotosTtlHours, googlePhotosRemainingHours));
  useEffect(() => {
    googlePhotosSettingsRef.current = getGooglePhotosSettings(googlePhotosTtlHours, googlePhotosRemainingHours);
  }, [googlePhotosTtlHours, googlePhotosRemainingHours]);

  // Remove any stale customizationData from localStorage on mount
  useEffect(() => {
    clearLegacyCustomizationData();
  }, []);

  const sendToHost = useCallback(
    ({ type, payload }: { type: string; payload?: object }) => {
      if (window.GooglePhotosBridge != null) {
        window.GooglePhotosBridge.postMessage(JSON.stringify({ type, ...payload }));
        return;
      }

      window.parent.postMessage({ type, ...payload }, origin);
    },
    [origin],
  );

  const isValidHostOrigin = useCallback((event: MessageEvent) => {
    // Structural origin validation first
    if (!event.origin || event.origin === 'null') {
      console.warn('Blocking message. Received message without an origin.');
      return false;
    }

    // If config isn't loaded yet or is explicitly empty, fail closed (drop the message)
    // This code should not be hit, because before we call this function, we check if this
    // is set, and if it is not, we wait to process the message until after it has been loaded
    // This is also called by fileSelection listeners in other files, but we should always
    // have target origin settings available by then.
    const targetOriginSetting = targetOriginSettingRef.current;
    if (!targetOriginSetting) {
      console.warn('Blocking message. Origin settings uninitialized.');
      return false;
    }

    // Evaluate origin if we're not on mobile, because on mobile we don't have a good
    // origin check. TODO: remove checking for '*' here after all project settings are
    // updated with real origins, now that mobile handling is separate.
    if (!window.GooglePhotosBridge && targetOriginSetting !== '*') {
      const validTargetOrigins = targetOriginSetting.split(',').map((o) => o.trim().toLowerCase());

      if (!validTargetOrigins.includes(event.origin.toLowerCase())) {
        console.warn('Blocking message. Origin unauthorized', event.origin);
        return false;
      }
    }

    // This is our only real check on mobile (checking on types), so just do it always.
    return event.data?.type === 'fileSelection';
  }, []);

  const processTrustedMessage = useCallback((event: MessageEvent) => {
    // Unpack data only after the origin has proven safe
    if (!event.data) return;

    const { type, files, title, coverPhotoId, action } = event.data;
    setOrigin((currentOrigin) => (currentOrigin === '*' ? event.origin : currentOrigin));

    if (type === 'fileSelection') {
      // ttl hours is the time before the photos expire on Google's side.
      // remaining hours is how much sooner we want to expire them on our side (to make sure we never hit Google's expiration).
      // For example, if ttlHours is 12 and remainingHours is 4, it means Google expires the urls after 12 hours, but we consider
      // them expired after 8.
      const expirationHours = googlePhotosSettingsRef.current.ttlHours - googlePhotosSettingsRef.current.remainingHours;
      const expirationMS = expirationHours * 60 * 60 * 1000;
      const expirationDate = new Date(Date.now() + expirationMS).toISOString();
      files.forEach((file: { expirationDate: string }) => {
        file.expirationDate = expirationDate;
      });

      // Skip storage when this fileSelection is a response to a refreshPhotos request
      // (e.g. cart or product-customize page refreshing expired URLs)
      if (action !== 'refreshPhotos') {
        const parsedCustomizationData = getCustomizationData();
        const existingPhotos: { id: string; url: string }[] = parsedCustomizationData?.photoInfos || [];

        // Index existing photos by id for O(1) lookups during merge
        const photoMap = new Map(existingPhotos.map((p) => [p.id, p]));

        for (const file of files) {
          const existing = photoMap.get(file.id);
          if (!existing || existing.url !== file.url) {
            // New photo or URL changed — insert/replace (new expiration date)
            photoMap.set(file.id, file);
          }
          // Same URL → keep existing entry (retains old expiration date)
        }

        const updatedPhotos = Array.from(photoMap.values());

        if (coverPhotoId) {
          const coverPhotoIndex = updatedPhotos.findIndex((p) => p.id === coverPhotoId);
          if (coverPhotoIndex > 0) {
            const [coverPhoto] = updatedPhotos.splice(coverPhotoIndex, 1);
            updatedPhotos.unshift(coverPhoto);
          }
        }

        setCustomizationData({
          ...parsedCustomizationData,
          ...(typeof title === 'string' && title.trim().length > 0 && { title: title.trim() }),
          photoInfos: updatedPhotos,
        });
      }
    }

    messageHandlersRef.current.forEach((handler) => handler(event));
  }, []);

  const subscribeToHostMessages = useCallback((handler: HostMessageHandler) => {
    messageHandlersRef.current.add(handler);
    return () => messageHandlersRef.current.delete(handler);
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // If the list of origins that are allowed to send us messages has
      // not loaded yet, we need to push all messages to a queue to be
      // processed once that data has loaded
      if (!targetOriginSettingRef.current) {
        if (pendingMessagesRef.current.length < MAX_PENDING_MESSAGES) {
          pendingMessagesRef.current.push(event);
        } else {
          console.warn('Blocking message. Pending message queue is full.');
        }
        return;
      }

      if (isValidHostOrigin(event)) {
        processTrustedMessage(event);
      }
    },
    [isValidHostOrigin, processTrustedMessage],
  );

  useEffect(() => {
    // Initialize photoInfos
    const parsedCustomizationData = getCustomizationData();
    if (!parsedCustomizationData.photoInfos) {
      setCustomizationData({
        ...parsedCustomizationData,
        photoInfos: [],
      });
    }

    window.addEventListener('message', handleMessage);

    // We can only send a ready message when we have a handler ready,
    // which is why we can't move it out to a separate useEffect
    if (!initialized) {
      sendToHost({ type: 'fujifilmStorefrontReady' });
      setInitialized(true);
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage, initialized, sendToHost]);

  useEffect(() => {
    // Process any pending messages that may have been queued
    // while we were waiting for targetOriginSetting to load
    if (targetOriginSetting) {
      const pendingMessages = pendingMessagesRef.current;
      pendingMessagesRef.current = [];
      pendingMessages.forEach(handleMessage);
    }
  }, [targetOriginSetting, handleMessage]);

  const sendRefreshPhotos = useCallback(
    (preselectedPhotoIds: string[]) => sendToHost({ type: 'refreshPhotos', payload: { preselectedPhotoIds } }),
    [sendToHost],
  );

  const sendCheckoutStart = useCallback(
    (flow: CheckoutFlow) => sendToHost({ type: 'checkoutStart', payload: { flow } }),
    [sendToHost],
  );

  const sendCheckoutComplete = useCallback(() => sendToHost({ type: 'checkoutComplete' }), [sendToHost]);

  const sendCartStatus = useCallback(
    (itemCount: number) =>
      sendToHost({
        type: 'cartStatus',
        payload: { isActive: itemCount > 0, itemCount },
      }),
    [sendToHost],
  );

  const sendConfirmBeforeClose = useCallback(
    (state: boolean) => sendToHost({ type: 'fujifilmConfirmBeforeClose', payload: { state } }),
    [sendToHost],
  );

  const sendExitStore = useCallback(() => {
    clearCustomizationData();
    if (globalThis.window !== undefined) {
      globalThis.sessionStorage.removeItem('p2r-store-results');
      globalThis.sessionStorage.removeItem('recentSearches');
    }
    sendToHost({ type: 'fujifilmExitStore' });
  }, [sendToHost]);

  const value = useMemo<MessagePortContextType>(
    () => ({
      isReady: initialized,
      sendToHost,
      sendCheckoutStart,
      sendCheckoutComplete,
      sendCartStatus,
      sendConfirmBeforeClose,
      sendRefreshPhotos,
      sendExitStore,
      subscribeToHostMessages,
    }),
    [
      initialized,
      sendToHost,
      sendCheckoutStart,
      sendCheckoutComplete,
      sendCartStatus,
      sendConfirmBeforeClose,
      sendRefreshPhotos,
      sendExitStore,
      subscribeToHostMessages,
    ],
  );

  return <MessagePortContext.Provider value={value}>{children}</MessagePortContext.Provider>;
};

export const useMessagePort = () => {
  const context = useContext(MessagePortContext);
  //  Custom hook handles the undefined check for type safety
  if (!context) {
    throw new Error('useMessagePort must be used within a MessageChannelPortProvider');
  }
  return context;
};
